import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import axiosInstance from '../utils/axios';
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  Select,
  message,
  Card,
  Space,
  Tag
} from 'antd';
import {
  TeamOutlined,
  PlusOutlined,
  SearchOutlined,
  HomeOutlined,
  UserOutlined,
  IdcardOutlined,
  LockOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { hssColors, commonStyles } from '../config/theme';
import { isAdmin } from '../utils/auth';

const Usuarios = () => {
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    cargarUsuarios();
  }, []);

  const cargarUsuarios = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/usuarios');
      setUsuarios(response.data);
    } catch (error) {
      message.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    try {
      console.log('Datos del usuario a crear:', values);
      const response = await axiosInstance.post('/usuarios', values);
      console.log('Respuesta al crear usuario:', response.data);
      message.success('Usuario creado exitosamente');
      setModalVisible(false);
      cargarUsuarios();
      form.resetFields();
    } catch (error) {
      console.error('Error al crear usuario:', error);
      message.error(error.response?.data?.error || 'Error al crear usuario');
    }
  };

  const filteredUsuarios = usuarios.filter(usuario => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return (
      usuario.dni?.toString().toLowerCase().includes(searchLower) ||
      usuario.nombre?.toLowerCase().includes(searchLower) ||
      usuario.usuario?.toLowerCase().includes(searchLower)
    );
  });

  const columns = [
    {
      title: 'CI',
      dataIndex: 'dni',
      key: 'dni',
      render: (dni) => (
        <Space>
          <IdcardOutlined style={{ color: hssColors.primary }} />
          <span style={{ fontWeight: '500' }}>{dni}</span>
        </Space>
      ),
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      render: (nombre) => (
        <Space>
          <UserOutlined style={{ color: hssColors.success }} />
          <span style={{ fontWeight: '500' }}>{nombre}</span>
        </Space>
      ),
    },
    {
      title: 'Usuario',
      dataIndex: 'usuario',
      key: 'usuario',
      render: (usuario) => (
        <span style={{
          padding: '4px 12px',
          background: hssColors.gray100,
          borderRadius: '6px',
          fontFamily: 'monospace',
          fontWeight: '600',
          color: hssColors.primary,
        }}>
          {usuario}
        </span>
      ),
    },
    {
      title: 'Rol',
      dataIndex: 'rol',
      key: 'rol',
      render: (rol) => {
        const cfg = {
          admin: { color: 'gold', label: 'Administrador' },
          administrativo: { color: 'blue', label: 'Administrativo' },
          tecnico: { color: 'green', label: 'Técnico' },
        }[rol] || { color: 'default', label: rol };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: hssColors.backgroundPrimary,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{ background: hssColors.backgroundSecondary, borderBottom: `1px solid ${hssColors.gray200}`, padding: '16px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <TeamOutlined style={{ fontSize: 28, color: hssColors.primary }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: hssColors.primary }}>Gestión de Usuarios</h1>
              <p style={{ margin: 0, fontSize: 13, color: hssColors.textSecondary }}>Administración de usuarios del sistema</p>
            </div>
          </div>
          <Space>
            <Button icon={<HomeOutlined />} onClick={() => navigate('/dashboard')} size="large" style={{ height: 44, borderRadius: 8 }}>Inicio</Button>
            {isAdmin() && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)} size="large" style={{ height: 44, borderRadius: 8, fontWeight: 600 }}>
                Nuevo Usuario
              </Button>
            )}
          </Space>
        </div>
      </div>

      {/* Contenido principal */}
      <div style={{ padding: '24px 32px', flex: 1 }}>
        {/* Stats */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Total usuarios', value: usuarios.length, icon: <TeamOutlined />, color: hssColors.primary },
            { label: 'Administradores', value: usuarios.filter(u => u.rol === 'admin').length, icon: <UserOutlined />, color: '#e67e22' },
            { label: 'Técnicos', value: usuarios.filter(u => u.rol === 'tecnico').length, icon: <UserOutlined />, color: hssColors.success },
            { label: 'Administrativos', value: usuarios.filter(u => u.rol === 'administrativo').length, icon: <UserOutlined />, color: hssColors.textSecondary },
          ].map(s => (
            <Card key={s.label} loading={loading} style={{ flex: 1, borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 28, color: s.color }}>{s.icon}</div>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: hssColors.textSecondary }}>{s.label}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Búsqueda */}
        <Card style={{ marginBottom: '24px', borderRadius: '12px', border: 'none', boxShadow: commonStyles.shadowMd }} styles={{ body: { padding: '20px 24px' } }}>
          <Input
            placeholder="Buscar usuario por nombre, CI o usuario..."
            prefix={<SearchOutlined style={{ color: hssColors.gray400 }} />}
            style={{ width: '100%', borderRadius: '8px' }}
            size="large"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
        </Card>

        {/* Tabla de usuarios */}
        <Card
          style={{
            borderRadius: '12px',
            border: 'none',
            boxShadow: commonStyles.shadowMd,
          }}
          styles={{
            body: { padding: 0 }
          }}
        >
          <Table
            columns={columns}
            dataSource={filteredUsuarios}
            rowKey="dni"
            scroll={{ x: 'max-content' }}
            pagination={{
              current: paginaActual,
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (p, size) => { setPaginaActual(p); setPageSize(size); },
              showTotal: (total) => `Total: ${total} usuarios`,
            }}
            style={{
              borderRadius: '12px',
              overflow: 'hidden',
            }}
          />
        </Card>
      </div>

      {/* Modal Nuevo Usuario */}
      <Modal
        title={
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '20px',
            fontWeight: '700',
            color: hssColors.primary,
          }}>
            <TeamOutlined />
            <span>Nuevo Usuario</span>
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={form} onFinish={onFinish} layout="vertical" style={{ marginTop: '24px' }}>
          <Form.Item
            name="dni"
            label="CI"
            rules={[{ required: true, message: 'Ingrese la CI' }]}
          >
            <Input
              prefix={<IdcardOutlined style={{ color: hssColors.gray400 }} />}
              placeholder="Número de CI"
              type="number"
              size="large"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item
            name="nombre"
            label="Nombre Completo"
            rules={[{ required: true, message: 'Ingrese el nombre' }]}
          >
            <Input
              prefix={<UserOutlined style={{ color: hssColors.gray400 }} />}
              placeholder="Nombre completo del usuario"
              size="large"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item
            name="usuario"
            label="Nombre de Usuario"
            rules={[{ required: true, message: 'Ingrese el nombre de usuario' }]}
          >
            <Input
              prefix={<UserOutlined style={{ color: hssColors.gray400 }} />}
              placeholder="Nombre de usuario para login"
              size="large"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item
            name="contrasena"
            label="Contraseña"
            rules={[
              { required: true, message: 'Ingrese la contraseña' },
              { min: 6, message: 'La contraseña debe tener al menos 6 caracteres' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: hssColors.gray400 }} />}
              placeholder="Contraseña (mínimo 6 caracteres)"
              size="large"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item
            name="rol"
            label="Rol"
            initialValue="administrativo"
            rules={[{ required: true, message: 'Seleccione un rol' }]}
          >
            <Select size="large" style={{ borderRadius: '8px' }}>
              <Select.Option value="admin">Administrador</Select.Option>
              <Select.Option value="administrativo">Administrativo</Select.Option>
              <Select.Option value="tecnico">Técnico</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => setModalVisible(false)}
                size="large"
                style={{ borderRadius: '8px' }}
              >
                Cancelar
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                style={{
                  borderRadius: '8px',
                  fontWeight: '600',
                  boxShadow: commonStyles.shadowMd,
                }}
              >
                Guardar Usuario
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      <Footer />
    </div>
  );
};

export default Usuarios;